import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import "@/App.css";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import PostAd from "@/pages/PostAd";
import AdDetail from "@/pages/AdDetail";
import Wallet from "@/pages/Wallet";
import AdminDashboard from "@/pages/AdminDashboard";
import AgentDashboard from "@/pages/AgentDashboard";
import CityServicePage from "@/pages/CityServicePage";
import SeoCategoryPage from "@/pages/SeoCategoryPage";
import SeoAdPage from "@/pages/SeoAdPage";
import AllCities from "@/pages/AllCities";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Analytics from "@/pages/Analytics";
import { Toaster } from "@/components/ui/sonner";
import { useEffect, useState } from "react";
import { api } from "@/utils/api";
import { Helmet } from "react-helmet-async";


const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

const BrandHelmet = () => {
  const [branding, setBranding] = useState({});

  useEffect(() => {
    const loadBranding = async () => {
      try {
        const res = await api.get("/site-branding");
        setBranding(res.data || {});
      } catch (e) {
        // ignore
      }
    };
    loadBranding();
  }, []);

  if (!branding?.favicon_url) return null;

  return (
    <Helmet>
      <link rel="icon" href={branding.favicon_url} />
    </Helmet>
  );
};

function App() {
  return (
    <AuthProvider>
      <Toaster />
      <BrandHelmet />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/cities" element={<AllCities />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/:city" element={<AllCities />} />
          
          {/* SEO routes */}
          <Route path="/india/:category" element={<SeoCategoryPage />} />
          <Route path="/:city/:category/:slug" element={<SeoAdPage />} />
          <Route path="/:city/:category" element={<SeoCategoryPage />} />

          {/* Legacy routes */}
          <Route path="/ad/:adId" element={<AdDetail />} />

          {/* Backwards compatible: keep old CityServicePage accessible if needed */}
          <Route path="/legacy/:city/:category" element={<CityServicePage />} />
          
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/post-ad"
            element={
              <PrivateRoute>
                <PostAd />
              </PrivateRoute>
            }
          />
          <Route
            path="/wallet"
            element={
              <PrivateRoute>
                <Wallet />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute allowedRoles={["super_admin", "sub_admin"]}>
                <AdminDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/agent"
            element={
              <PrivateRoute allowedRoles={["agent", "super_admin"]}>
                <AgentDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <PrivateRoute>
                <Analytics />
              </PrivateRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
